import { roundMoney } from '@renderer/utils/formatCurrency'
import { parsePointCancelResponse, type PointCancelOutcome } from '@renderer/utils/pointPayment'
import type { PaymentProvider } from '@renderer/utils/paymentProvider'

interface PaymentResponse {
  order_id: string
  wallet_id?: string
  qr_url?: string // Optional for Snap payments
  snap_token?: string // For Snap payments
  redirect_url?: string // Optional for ToyyibPay redirect payments
  expiry_time: string
  // The amount the gateway actually bills, in `charge_currency`.
  // Midtrans charges in IDR (integer), ToyyibPay charges in MYR (2 decimals),
  // Tap charges in whichever GCC currency the operator's wallet is set to
  // (SAR/AED/QAR = 2 decimals, KWD/BHD/OMR = 3 decimals).
  amount: number
  // Currency the `amount`/`charge_currency` field is denominated in.
  currency?: string
  /** ISO-4217 currency the customer is charged in (IDR for cross-border QRIS). */
  charge_currency?: string
  /** Project-native amount the price was quoted in (e.g. RM for MY projects). */
  native_amount?: number
  /** Native currency of `native_amount`. */
  native_currency?: string
  /** MYR→IDR rate used for the QRIS charge (present only when converted). */
  fx_rate?: number
  /** "as of" timestamp of `fx_rate`. */
  fx_as_of?: string
  /** Whether `fx_rate` is live or the safety fallback. */
  fx_source?: 'live' | 'fallback'
  server_time: string
  /** `terminal` = the amount was pushed to the booth's connected card terminal. */
  payment_type: 'core' | 'snap' | 'redirect' | 'terminal'
  provider?: PaymentProvider
  client_key?: string // For Snap payments
  /**
   * Provider-side reference. For a terminal payment this is the Mercado Pago
   * order id, echoed back as `?ref=` on status and cancel calls.
   */
  gateway_reference?: string
  /** Terminal the charge was pushed to (terminal payments only). */
  terminal_id?: string
}

interface PaymentStatusResponse {
  paid: boolean
  serverTime: string
  isExpired: boolean
  transactionStatus: string
  /** Card-terminal order status; `null` for every other payment type. */
  pointStatus: string | null
}

/**
 * Thrown when the backend rejects a Midtrans/QRIS charge because the
 * project's country is in a voucher-only tier (`localized_in_progress` or
 * `global_hidden`) and the operator hasn't wired a custom gateway. The
 * photobooth's payment page should switch to voucher-only UI on this error
 * rather than retrying.
 */
export class VoucherOnlyRegionError extends Error {
  constructor(public readonly paymentSetupTier?: string) {
    super('Payment region is voucher-only')
    this.name = 'VoucherOnlyRegionError'
  }
}

/** `?ref=<Mercado Pago order id>` for card-terminal payments, empty otherwise. */
function referenceQuery(reference?: string | null): string {
  return reference ? `?ref=${encodeURIComponent(reference)}` : ''
}

export class MidtransUtils {
  private static apiUrl = `${process.env.VITE_API_URL}`

  public static async initiatePayment(
    grossAmount: number,
    id: string,
    expiryDuration: number,
    projectId: string,
    filterId: string,
    frameId: string,
    discountAmount: number,
    isExtraPrint: boolean = false,
    provider: PaymentProvider = 'midtrans',
    isAiEffect: boolean = false,
    /**
     * `terminalId`: the card terminal this booth is paired with. Studio uses
     * it only when the project's gateway is Mercado Pago, so a booth carried
     * over to another project keeps working without clearing the setting.
     * `terminalProvider: 'nayax'` says the terminal is a Nayax reader, which
     * Studio charges whatever the project's gateway is.
     */
    options: { terminalId?: string; terminalProvider?: 'nayax' } = {}
  ): Promise<PaymentResponse> {
    try {
      // Amounts are in the project's native currency; the backend converts to
      // the gateway's settlement currency (and rounds to its unit) itself. We
      // only round to 2 decimals here to strip float noise without dropping
      // cents — an integer round would lose ringgit/dollar cents.
      const finalAmount = roundMoney(Math.max(0, grossAmount), 'MYR')
      const roundedDiscountAmount = roundMoney(Math.max(0, discountAmount), 'MYR')

      const response = await fetch(`${this.apiUrl}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        cache: 'no-cache',
        body: JSON.stringify({
          grossAmount: finalAmount, // Native amount, after discount
          id,
          expiryDuration,
          projectId,
          filterId,
          frameId,
          discountAmount: roundedDiscountAmount,
          isExtraPrint,
          isAiEffect,
          provider,
          ...(options.terminalId ? { terminalId: options.terminalId } : {}),
          ...(options.terminalId && options.terminalProvider
            ? { terminalProvider: options.terminalProvider }
            : {})
        })
      })

      if (!response.ok) {
        // Read the JSON error body ONCE, for every non-ok status. The studio
        // API always answers with `{ error, message }`, whereas
        // `response.statusText` is an empty string over HTTP/2 — which is why
        // booth activity logs recorded a useless bare "Payment initiation
        // failed:" for every failed checkout, leaving support with nothing to
        // diagnose from.
        let body: { error?: string; message?: string; paymentSetupTier?: string } | null = null
        try {
          body = await response.json()
        } catch {
          // Non-JSON error page (proxy/CDN); fall through to the status code.
        }

        // 409 voucher_only_region: backend refused QRIS for this project's
        // country. Throw a typed error so the caller can switch UI rails
        // without parsing free-text messages.
        if (response.status === 409 && body?.error === 'voucher_only_region') {
          throw new VoucherOnlyRegionError(body.paymentSetupTier)
        }

        const reason = body?.message || body?.error || response.statusText || 'no response body'
        throw new Error(`Payment initiation failed (HTTP ${response.status}): ${reason}`)
      }

      const data = await response.json()
      // Round the billed amount to its currency's own minor-unit precision:
      // 0 decimals for IDR, 3 for Tap's KWD/BHD/OMR (fils/baisa), 2 for
      // everything else (MYR, MXN, most Tap GCC currencies, …).
      //
      // `currency` is optional on the response, so we also fall back on
      // `provider` (toyyibpay always bills in MYR, mercadopago in MXN,
      // razorpay in INR) for
      // gateways whose settlement currency is fixed — Tap is multi-currency,
      // so it has no such fallback and relies on `charge_currency`/`currency`
      // being present. Casing is normalised because backends and proxies can
      // mutate string casing.
      const declaredCurrency =
        typeof data?.charge_currency === 'string'
          ? data.charge_currency.toUpperCase()
          : typeof data?.currency === 'string'
            ? data.currency.toUpperCase()
            : ''
      const declaredProvider =
        typeof data?.provider === 'string' ? data.provider.toLowerCase() : ''
      const effectiveCurrency =
        declaredCurrency ||
        (declaredProvider === 'toyyibpay'
          ? 'MYR'
          : declaredProvider === 'mercadopago'
            ? 'MXN'
            : declaredProvider === 'razorpay'
              ? 'INR'
              : '')
      return {
        ...data,
        amount: roundMoney(Number(data.amount) || 0, effectiveCurrency || 'IDR')
      }
    } catch (error) {
      console.error('Error initiating payment:', error)
      throw error
    }
  }

  public static async checkPaymentStatus(
    orderId: string,
    options: { reference?: string | null } = {}
  ): Promise<PaymentStatusResponse> {
    try {
      const response = await fetch(
        `${this.apiUrl}/api/payments/${orderId}${referenceQuery(options.reference)}`,
        {
          method: 'GET',
          cache: 'no-cache',
          credentials: 'include'
        }
      )

      if (!response.ok) {
        throw new Error(`Payment status check failed: ${response.statusText}`)
      }

      const data = await response.json()
      return {
        paid: data.simplifiedResponse.paid,
        serverTime: data.simplifiedResponse.transaction_time || new Date().toISOString(),
        isExpired: data.simplifiedResponse.isExpired,
        transactionStatus: data.simplifiedResponse.transaction_status,
        pointStatus: data.simplifiedResponse.point_status ?? null
      }
    } catch (error) {
      console.error('Error checking payment status:', error)
      throw error
    }
  }

  /**
   * Cancel a pending charge and report whether the booth may leave the payment
   * screen. Used for card-terminal payments, where Mercado Pago can only cancel
   * an order the terminal has not picked up yet.
   */
  public static async cancelPayment(
    orderId: string,
    options: { reference?: string | null } = {}
  ): Promise<PointCancelOutcome> {
    try {
      const response = await fetch(
        `${this.apiUrl}/api/payments/${orderId}${referenceQuery(options.reference)}`,
        { method: 'DELETE', credentials: 'include' }
      )
      const body: unknown = await response.json().catch(() => null)
      return parsePointCancelResponse(response.status, body)
    } catch (error) {
      console.error('Error cancelling payment:', error)
      return 'unknown'
    }
  }
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    snap: {
      pay
    }
  }
}

export default MidtransUtils
